import type { LineItemInput, GlobalDiscountInput } from "../types";

interface TotalsPreviewProps {
  items: LineItemInput[];
  globalDiscounts?: GlobalDiscountInput[];
}

const IVA_RATE = 19;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(value);
}

export function TotalsPreview({
  items,
  globalDiscounts = [],
}: TotalsPreviewProps) {
  // Calculate line item totals
  let taxableTotal = 0;
  let exemptTotal = 0;

  for (const item of items) {
    const base = item.cantidad * item.precioUnitario;
    const discount = item.porcentajeDescuento
      ? base * (item.porcentajeDescuento / 100)
      : 0;
    const subtotal = Math.round(base - discount);

    if (item.exento) {
      exemptTotal += subtotal;
    } else {
      taxableTotal += subtotal;
    }
  }

  // Apply global discounts
  for (const gd of globalDiscounts) {
    const isPercentage = gd.tipoValor === "%";
    const appliesToTaxable = gd.indicadorExencion !== 2;
    const appliesToExempt = gd.indicadorExencion === 2;

    if (gd.tipo === "D") {
      // Discount
      if (appliesToTaxable) {
        const discountAmount = isPercentage
          ? Math.round(taxableTotal * (gd.valor / 100))
          : Math.round(gd.valor);
        taxableTotal -= discountAmount;
      }
      if (appliesToExempt) {
        const discountAmount = isPercentage
          ? Math.round(exemptTotal * (gd.valor / 100))
          : Math.round(gd.valor);
        exemptTotal -= discountAmount;
      }
    } else {
      // Surcharge
      if (appliesToTaxable) {
        const surchargeAmount = isPercentage
          ? Math.round(taxableTotal * (gd.valor / 100))
          : Math.round(gd.valor);
        taxableTotal += surchargeAmount;
      }
      if (appliesToExempt) {
        const surchargeAmount = isPercentage
          ? Math.round(exemptTotal * (gd.valor / 100))
          : Math.round(gd.valor);
        exemptTotal += surchargeAmount;
      }
    }
  }

  const iva = Math.round(taxableTotal * (IVA_RATE / 100));
  const total = taxableTotal + iva + exemptTotal;

  return (
    <div className="bg-muted/30 rounded-lg p-4">
      <h3 className="text-sm font-medium mb-3">Totales</h3>
      <div className="space-y-2 text-sm">
        {taxableTotal > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Neto:</span>
            <span>{formatCurrency(taxableTotal)}</span>
          </div>
        )}
        {exemptTotal > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Exento:</span>
            <span>{formatCurrency(exemptTotal)}</span>
          </div>
        )}
        {taxableTotal > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">IVA {IVA_RATE}%:</span>
            <span>{formatCurrency(iva)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t font-medium">
          <span>TOTAL:</span>
          <span className="text-lg">{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );
}
